import { memo, useState, useCallback } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GitBranch, Shield, Pencil, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useAppStore } from '@/stores/app-store';
import { useUIStore } from '@/stores/ui-store';
import { EVENT_APPS } from '@/config/constants';
import type { ConditionNodeData, MMMCard } from '@/types/field-mapping-flow';

function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function ConditionNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as ConditionNodeData;
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const selectBranch = useFlowBuilderStore((s) => s.selectBranch);
  const dirtyBranches = useFlowBuilderStore((s) => s.dirtyBranches);
  const setEditingConditionId = useFlowBuilderStore((s) => s.setEditingConditionId);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);
  const isDark = useUIStore((s) => s.theme) === 'dark';

  const isSelected = selectedBranchId === nodeData.docId;
  const isDirty = nodeData.docId ? dirtyBranches.has(nodeData.docId) : false;

  const handleClick = () => {
    selectBranch(nodeData.docId);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEventApp && nodeData.docId) {
      setEditingConditionId(nodeData.docId);
    }
  };

  const setActiveSidebarSection = useFlowBuilderStore((s) => s.setActiveSidebarSection);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeData.docId) {
      selectBranch(nodeData.docId);
      setEditingConditionId(nodeData.docId);
    }
  };

  const addCard = useFlowBuilderStore((s) => s.addCard);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleAddAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeData.docId) {
      selectBranch(nodeData.docId);
      setActiveSidebarSection('modules');
    }
  };

  const handleNodeDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  }, []);

  const handleNodeDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleNodeDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const moduleDataStr = e.dataTransfer.getData('application/flow-module');
    if (!moduleDataStr || !nodeData.docId) return;

    try {
      const { moduleValue, moduleLabel } = JSON.parse(moduleDataStr) as {
        moduleValue: string;
        moduleLabel: string;
      };

      // Prevent duplicate modules on this branch
      const store = useFlowBuilderStore.getState();
      const mapping = store.fieldMappings[nodeData.docId];
      if (mapping?.fields && mapping.isMMM) {
        const existingCards = Object.values(mapping.fields).filter(
          (val): val is MMMCard =>
            typeof val === 'object' && val !== null && 'cardId' in val
        );
        if (existingCards.some((c) => c.saasFieldModuleName === moduleValue)) return;
      }

      const cardId = makeRandomId(7);
      const newCard: MMMCard = {
        cardId,
        saasFieldModuleName: moduleValue,
        displayLabel: moduleLabel,
        orderOf: 0,
        fields: {},
        fields_type: {},
        ct: Date.now(),
      };

      selectBranch(nodeData.docId);
      addCard(nodeData.docId, newCard);
    } catch {
      // ignore
    }
  }, [nodeData.docId, selectBranch, addCard]);

  const isDefault = nodeData.isDefault;

  // Colors — orange theme, dark-mode aware
  const iconBg = isDark ? '#ea580c' : '#ea580c';
  const labelColor = isDark ? '#fb923c' : '#ea580c';
  const cardBg = isDark
    ? 'linear-gradient(135deg, #431407 0%, #7c2d12 100%)'
    : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)';
  const cardBorder = isDark
    ? `1px solid ${isSelected ? '#f97316' : '#9a3412'}`
    : `1px solid ${isSelected ? '#fb923c' : '#fed7aa'}`;
  const handleColor = isDark ? '#f97316' : '#f97316';
  const dragOverBg = isDark
    ? 'linear-gradient(135deg, #7c2d12 0%, #9a3412 100%)'
    : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)';
  const dragOverBorder = isDark ? '1px solid #fb923c' : '1px solid #f97316';

  return (
    <div
      className={`
        group cursor-pointer rounded-xl px-4 py-3 shadow-md transition-all min-w-[220px] max-w-[260px]
        ${isSelected ? 'ring-2 ring-orange-300/40' : 'hover:shadow-lg'}
        ${isDragOver ? 'ring-2 ring-orange-400/60' : ''}
      `}
      style={{
        background: isDragOver ? dragOverBg : cardBg,
        border: isDragOver ? dragOverBorder : cardBorder,
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onDragOver={handleNodeDragOver}
      onDragLeave={handleNodeDragLeave}
      onDrop={handleNodeDrop}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: handleColor, background: isDark ? '#1c1917' : 'white' }}
      />

      <div className="flex items-center gap-2.5">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm"
          style={{ background: iconBg }}
        >
          {isDefault ? (
            <Shield className="h-4 w-4 text-white" />
          ) : (
            <GitBranch className="h-4 w-4 text-white" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: labelColor }}>
            {isDefault ? 'Default' : 'Condition'}
          </p>
          <p className="truncate text-xs font-semibold text-gray-900 dark:text-foreground">
            {nodeData.filterValueLabel}
          </p>
          {isDefault && (
            <p className="mt-0.5 text-[10px] leading-tight text-gray-500 dark:text-gray-400">
              Runs if other branches aren&apos;t matched or not configured
            </p>
          )}
        </div>

        {/* Edit button (visible on hover, messaging apps only) */}
        {!isEventApp && (
          <button
            onClick={handleEditClick}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100 dark:text-gray-500 dark:hover:text-gray-300"
            style={{ background: 'transparent' }}
            title="Edit condition"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>

      {isDirty && (
        <Badge variant="secondary" className="mt-2 text-[10px]">
          Unsaved
        </Badge>
      )}

      {/* Add Action button — events apps with no actions configured */}
      {isEventApp && !nodeData.hasActions && (
        <button
          onClick={handleAddAction}
          className="mt-2.5 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-[10px] font-medium transition-colors"
          style={{
            border: isDark ? '1px dashed #78716c' : '1px dashed #d6d3d1',
            color: isDark ? '#a8a29e' : '#78716c',
          }}
        >
          <Plus className="h-3 w-3" />
          Add Action
        </button>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: handleColor, background: isDark ? '#1c1917' : 'white' }}
      />
    </div>
  );
}

export const ConditionNode = memo(ConditionNodeComponent);
